import { useEffect, type RefObject } from 'react'
import { focusableIn } from '../a11y/focusable'

/**
 * Hold Tab inside a subtree while it is active.
 *
 * **Its own module, not folded into `Overlay`.** The wrap arithmetic is the part
 * that is easy to get subtly wrong and hard to see through a portal, so it is
 * testable with a plain `<div>` and no portal in the way.
 *
 * **`aria-modal` is not this.** That attribute is a promise to assistive
 * technology that a trap exists; it moves no focus and stops no Tab. A dialog
 * carrying it with nothing else is a dialog a keyboard user walks straight out
 * of, into a background they cannot see. `Overlay` sets both — the attribute
 * for the accessibility tree, this hook for the behaviour.
 *
 * **Not `useFocusRequest`.** That hook moves focus TO a named ref after an async
 * step and consumes the request; this one keeps focus WITHIN a subtree for as
 * long as it is open. `Overlay` uses both mechanisms for different jobs — see
 * its note on restoring focus to the trigger.
 */

// The selector and the filter moved to `a11y/focusable.ts` when the F6 rotor
// needed the same idea. Copying them would have been the three-implementations
// failure `focusFirstAlive` in that directory exists to record.

export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    const container = ref.current
    if (!active || container === null) return

    const items = focusableIn(container)
    if (items.length > 0) {
      items[0]?.focus()
    } else {
      // An empty trap is a real state — every control disabled in flight
      // reaches it. Leaving focus on <body> would put the next Tab at the top
      // of the background document, which is the bug the trap exists to stop.
      container.tabIndex = -1
      container.focus()
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab' || container === null) return
      // Re-read on every keystroke rather than closing over the list. A confirm
      // dialog enables and disables its buttons mid-flight, so a list captured
      // at open time wraps onto a control that is now disabled.
      const current = focusableIn(container)
      if (current.length === 0) {
        event.preventDefault()
        return
      }
      const first = current[0]!
      const last = current[current.length - 1]!
      const onFirst = document.activeElement === first
      const onLast = document.activeElement === last

      if (event.shiftKey && onFirst) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && onLast) {
        event.preventDefault()
        first.focus()
      }
    }

    // On the document, not the container: focus can be outside the subtree when
    // the key arrives — the browser blurs a disabled element to <body> — and a
    // container listener never sees that keystroke, which is the exact case
    // that lets focus escape.
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [ref, active])
}
